import { useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import "./GameProgressChart.css";

const HIDDEN_ITEMS_LS_KEY = "vocabularium_memory_chart_hidden";

function readHiddenItems() {
  try {
    const raw = localStorage.getItem(HIDDEN_ITEMS_LS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeHiddenItems(hiddenItems) {
  try {
    localStorage.setItem(HIDDEN_ITEMS_LS_KEY, JSON.stringify(hiddenItems));
  } catch {
    // quota / mode privé : on ignore
  }
}

const SCORE_SERIES = {
  key: "score",
  name: "Score de restitution",
  color: "#6b3fa0",
};

const THRESHOLD_LINES = [
  {
    key: "averageThreshold",
    value: 7.5,
    name: "Moyenne (7,5 / 15)",
    color: "#5b7a96",
  },
  {
    key: "excellentThreshold",
    value: 12,
    name: "Très bonne mémorisation (12 / 15)",
    color: "#1f6b45",
  },
];

const EMPTY_CHART_DATA = [
  { label: " ", score: 0, totalQuestions: 15, placeholder: true },
  { label: "  ", score: 0, totalQuestions: 15, placeholder: true },
  { label: "   ", score: 0, totalQuestions: 15, placeholder: true },
];

const SKELETON_CHART_DATA = [
  { label: "00/00 00:00", score: 0, totalQuestions: 15, placeholder: true },
  { label: "00/00 00:00", score: 0, totalQuestions: 15, placeholder: true },
  { label: "00/00 00:00", score: 0, totalQuestions: 15, placeholder: true },
  { label: "00/00 00:00", score: 0, totalQuestions: 15, placeholder: true },
  { label: "00/00 00:00", score: 0, totalQuestions: 15, placeholder: true },
  { label: "00/00 00:00", score: 0, totalQuestions: 15, placeholder: true },
  { label: "00/00 00:00", score: 0, totalQuestions: 15, placeholder: true },
  { label: "00/00 00:00", score: 0, totalQuestions: 15, placeholder: true },
];

function formatPointLabel(point, index) {
  if (point?.playedAt) {
    const date = new Date(point.playedAt);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  }
  return `Partie ${index + 1}`;
}

function MemoryScoreTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  const scorePoint = payload.find((entry) => entry.dataKey === SCORE_SERIES.key);
  if (!scorePoint || scorePoint.payload?.placeholder || scorePoint.value == null) return null;

  const totalQuestions = scorePoint?.payload?.totalQuestions ?? 15;

  return (
    <div className="game-progress-chart__tooltip">
      <p className="game-progress-chart__tooltip-label">{label}</p>
      <p className="game-progress-chart__tooltip-row" style={{ color: scorePoint.color }}>
        {SCORE_SERIES.name}&nbsp;:{" "}
        <strong>
          {scorePoint.value} / {totalQuestions}
        </strong>
      </p>
    </div>
  );
}

function ThresholdLineLabel({ viewBox, text, fill }) {
  if (!viewBox) return null;

  return (
    <text
      x={viewBox.x + 8}
      y={viewBox.y + 8}
      fill={fill}
      fontSize={10}
      fontFamily="Georgia, serif"
      dominantBaseline="hanging"
    >
      {text}
    </text>
  );
}

function MemoryScoreLegend({ items, hiddenItems, onToggle }) {
  if (!items.length) return null;

  return (
    <div className="game-progress-chart__legend" role="list" aria-label="Légende du graphique">
      {items.map((item) => {
        const isHidden = Boolean(hiddenItems[item.key]);
        return (
          <button
            key={item.key}
            type="button"
            role="listitem"
            className={`game-progress-chart__legend-item${
              isHidden ? " game-progress-chart__legend-item--hidden" : ""
            }`}
            onClick={() => onToggle(item.key)}
            aria-pressed={!isHidden}
          >
            <span
              className={`game-progress-chart__legend-swatch${
                item.dashed ? " game-progress-chart__legend-swatch--dashed" : ""
              }`}
              style={{ borderTopColor: item.color }}
              aria-hidden="true"
            />
            <span>{item.name}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function MemoryScoreChart({ history = [], skeleton = false }) {
  const [hiddenItems, setHiddenItems] = useState(() => readHiddenItems());

  const chartData = useMemo(
    () =>
      (Array.isArray(history) ? history : []).map((point, index) => ({
        ...point,
        label: formatPointLabel(point, index),
        score: Number(point.score) || 0,
        totalQuestions: point.totalQuestions ?? 15,
        placeholder: false,
      })),
    [history]
  );

  const hasRealData = !skeleton && chartData.length > 0;
  const displayData = skeleton ? SKELETON_CHART_DATA : hasRealData ? chartData : EMPTY_CHART_DATA;

  const legendItems = useMemo(
    () => [
      { ...SCORE_SERIES, dashed: false },
      ...THRESHOLD_LINES.map((line) => ({ ...line, dashed: true })),
    ],
    []
  );

  const handleLegendToggle = (itemKey) => {
    setHiddenItems((prev) => {
      const next = { ...prev, [itemKey]: !prev[itemKey] };
      writeHiddenItems(next);
      return next;
    });
  };

  return (
    <section
      className={`game-progress-chart${skeleton ? " game-progress-chart--skeleton" : ""}`}
      aria-hidden={skeleton || undefined}
      aria-label={skeleton ? undefined : "Évolution des scores du jeu de mémoire"}
    >
      <MemoryScoreLegend
        items={legendItems}
        hiddenItems={skeleton ? {} : hiddenItems}
        onToggle={handleLegendToggle}
      />
      <div className="game-progress-chart__chart-wrap game-progress-chart__chart-wrap--memory">
        <ResponsiveContainer width="100%" height="100%" minHeight={220}>
          <LineChart
            data={displayData}
            margin={{ top: 12, right: 12, left: 4, bottom: 4 }}
            style={{ background: "transparent" }}
          >
            <CartesianGrid
              stroke="rgba(57, 33, 13, 0.12)"
              strokeDasharray="3 3"
              vertical={false}
            />
            <XAxis
              dataKey="label"
              tick={
                hasRealData || skeleton
                  ? { fill: "rgba(57, 33, 13, 0.72)", fontSize: 10 }
                  : false
              }
              tickLine={false}
              axisLine={{ stroke: "rgba(57, 33, 13, 0.28)" }}
              minTickGap={24}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fill: "rgba(57, 33, 13, 0.72)", fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: "rgba(57, 33, 13, 0.28)" }}
              width={36}
              allowDecimals={false}
              domain={[0, 15]}
              ticks={[0, 3, 6, 9, 12, 15]}
            />
            {skeleton ? null : <Tooltip content={<MemoryScoreTooltip />} />}
            {THRESHOLD_LINES.map((line) =>
              !skeleton && hiddenItems[line.key] ? null : (
                <ReferenceLine
                  key={line.key}
                  y={line.value}
                  stroke={line.color}
                  strokeDasharray="7 5"
                  strokeWidth={1.5}
                  strokeOpacity={0.85}
                  ifOverflow="extendDomain"
                  label={(labelProps) => (
                    <ThresholdLineLabel {...labelProps} text={line.name} fill={line.color} />
                  )}
                />
              )
            )}
            {hasRealData && !hiddenItems[SCORE_SERIES.key] ? (
              <Line
                type="monotone"
                dataKey={SCORE_SERIES.key}
                name={SCORE_SERIES.name}
                stroke={SCORE_SERIES.color}
                strokeWidth={2.5}
                dot={{ r: 3.5, stroke: "#faf0e6", strokeWidth: 1.5, fill: SCORE_SERIES.color }}
                activeDot={{ r: 5 }}
                isAnimationActive={false}
              />
            ) : (
              <Line
                type="monotone"
                dataKey={SCORE_SERIES.key}
                stroke="transparent"
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
